import { getTournamentEntryStars } from '../../../utils/playerStars';
import { setStageLabels } from '../tournament_api';

export const DOUBLE_ELIM_SIZES = [4, 8, 16, 32];

export const isDoubleElimSize = (maxPlayers) => DOUBLE_ELIM_SIZES.includes(Number(maxPlayers));

const winnerStageLabels = (maxPlayers) =>
    setStageLabels(maxPlayers)
        .filter((label) => label !== 'Third Place')
        .map((label) => (label === 'Final' ? 'WB Final' : label));

const loserStageLabels = (maxPlayers) => {
    const size = Number(maxPlayers);
    const loserStageCount = Math.max(1, 2 * Math.log2(size) - 2);

    if (loserStageCount <= 1) {
        return ['LB Final'];
    }

    return [...Array.from({ length: loserStageCount - 1 }, (_, index) => `LB R${index + 1}`), 'LB Final'];
};

export const getDoubleElimStageLabels = (maxPlayers) => [
    ...winnerStageLabels(maxPlayers),
    ...loserStageLabels(maxPlayers),
    'Grand Final'
];

const winnerPairsPerStage = (maxPlayers) => {
    const map = {
        '1/32 Final': 32,
        '1/16 Final': 16,
        '1/8 Final': 8,
        'Quarter-final': 4,
        'Semi-final': 2,
        'WB Final': 1
    };

    return winnerStageLabels(maxPlayers).map((label) => map[label] || 1);
};

const loserPairsPerStage = (maxPlayers) => {
    const size = Number(maxPlayers);
    const labels = loserStageLabels(size);
    const counts = [];
    let matches = size / 4;

    for (let index = 0; index < labels.length - 1; index++) {
        counts.push(matches);
        if (index % 2 === 1) {
            matches = Math.max(1, matches / 2);
        }
    }

    counts.push(1);
    return counts;
};

const createEmptyGames = (count) =>
    Array.from({ length: count }, (_, gameId) => ({
        castle1: '',
        castle2: '',
        castleWinner: '',
        gameId,
        gameStatus: 'Not Started',
        gameWinner: '',
        color1: 'red',
        color2: 'blue',
        gold1: 0,
        gold2: 0,
        restart1_111: 0,
        restart1_112: 0,
        restart2_111: 0,
        restart2_112: 0
    }));

const numericGameCount = (raw) => {
    const normalized = String(raw || '')
        .trim()
        .toLowerCase()
        .replace(/^bo-?/, '');
    const count = Number(normalized);
    return Number.isFinite(count) && count > 0 ? count : 0;
};

const toBoType = (raw) => {
    const count = numericGameCount(raw) || 1;
    return `bo-${count}`;
};

const resolveGameCount = (stageName, playoffsGames, tournamentPlayoffGamesFinal) => {
    if (stageName === 'WB Final' || stageName === 'Grand Final') {
        return numericGameCount(tournamentPlayoffGamesFinal) || numericGameCount(playoffsGames) || 1;
    }
    return numericGameCount(playoffsGames) || 1;
};

const resolveTypeOfGame = (stageName, playoffsGames, tournamentPlayoffGamesFinal) => {
    if (stageName === 'WB Final' || stageName === 'Grand Final') {
        return tournamentPlayoffGamesFinal || playoffsGames;
    }
    return playoffsGames;
};

const createEmptyPair = (stageName, playoffsGames, tournamentPlayoffGamesFinal) => {
    const gameCount = resolveGameCount(stageName, playoffsGames, tournamentPlayoffGamesFinal);
    const typeOfGame = resolveTypeOfGame(stageName, playoffsGames, tournamentPlayoffGamesFinal);
    return {
        stage: stageName,
        team1: 'TBD',
        team2: 'TBD',
        ratings1: null,
        ratings2: null,
        stars1: null,
        stars2: null,
        score1: 0,
        score2: 0,
        type: toBoType(typeOfGame),
        gameStatus: 'Not Started',
        games: createEmptyGames(gameCount),
        winner: null,
        color1: 'red',
        color2: 'blue',
        bracketSide:
            stageName === 'Grand Final' ? 'grand_final' : stageName.startsWith('LB') ? 'losers' : 'winners'
    };
};

const normalizePlayers = (shuffledNames) =>
    shuffledNames.map((player) => ({
        name: typeof player === 'string' ? player : player?.name || 'TBD',
        ratings: typeof player === 'string' ? '0' : player?.ratings || '0',
        stars: typeof player === 'string' ? 0 : getTournamentEntryStars(player?.stars)
    }));

export const createDoubleElimPlayoffPairs = (playoffsGames, tournamentPlayoffGamesFinal, shuffledNames, maxPlayers) => {
    const labels = getDoubleElimStageLabels(maxPlayers);
    const winnerCounts = winnerPairsPerStage(maxPlayers);
    const loserCounts = loserPairsPerStage(maxPlayers);
    let players = shuffledNames ? normalizePlayers(shuffledNames) : [];
    if (players.length === 0) {
        players = Array.from({ length: Number(maxPlayers) || 0 }, () => ({
            name: 'TBD',
            ratings: '0',
            stars: 0
        }));
    }
    const stages = [];

    labels.forEach((stageName, stageIndex) => {
        const isWinnerStage = stageIndex < winnerCounts.length;
        const pairCount =
            stageName === 'Grand Final'
                ? 1
                : isWinnerStage
                  ? winnerCounts[stageIndex]
                  : loserCounts[stageIndex - winnerCounts.length];
        const pairs = [];

        for (let pairIndex = 0; pairIndex < pairCount; pairIndex++) {
            const pair = createEmptyPair(stageName, playoffsGames, tournamentPlayoffGamesFinal);

            if (stageIndex === 0) {
                const player1 = players[pairIndex * 2];
                const player2 = players[pairIndex * 2 + 1];
                pair.team1 = player1?.name || 'TBD';
                pair.team2 = player2?.name || 'TBD';
                pair.ratings1 = player1?.ratings || '0';
                pair.ratings2 = player2?.ratings || '0';
                pair.stars1 = getTournamentEntryStars(player1?.stars);
                pair.stars2 = getTournamentEntryStars(player2?.stars);
            }

            pairs.push(pair);
        }

        stages.push(pairs);
    });

    return stages;
};

const fillSlot = (pair, slot, playerName, rating, stars) => {
    if (!pair || !playerName || playerName === 'TBD') {
        return false;
    }

    if (pair[slot] === 'TBD' || !pair[slot]) {
        pair[slot] = playerName;
        pair[slot === 'team1' ? 'ratings1' : 'ratings2'] = rating ?? pair[slot === 'team1' ? 'ratings1' : 'ratings2'];
        pair[slot === 'team1' ? 'stars1' : 'stars2'] = stars ?? pair[slot === 'team1' ? 'stars1' : 'stars2'];
        return true;
    }

    return false;
};

export const dropLoserToBracket = ({
    updatedPairs,
    stageLabels,
    currentStage,
    pairIndex,
    loser,
    loserRating,
    loserStars,
    maxPlayers
}) => {
    if (!loser || loser === 'TBD' || !isDoubleElimSize(maxPlayers)) {
        return false;
    }

    if (currentStage === 'WB Final') {
        const lbFinalIndex = stageLabels.indexOf('LB Final');
        return fillSlot(updatedPairs[lbFinalIndex]?.[0], 'team2', loser, loserRating, loserStars);
    }

    // 1-indexed winner-bracket round for the current stage (e.g. size 16: 1/8 Final=1, Quarter-final=2, ...)
    const winnerLabels = stageLabels.filter((label) => !label.startsWith('LB') && label !== 'Grand Final');
    const wbRound = winnerLabels.indexOf(currentStage) + 1;
    if (wbRound === 0) {
        return false;
    }

    if (wbRound === 1) {
        // First-round losers pair up with each other in LB R1
        const lbR1Index = stageLabels.indexOf('LB R1');
        const targetPairIndex = Math.floor(pairIndex / 2);
        const slot = pairIndex % 2 === 0 ? 'team1' : 'team2';
        return fillSlot(updatedPairs[lbR1Index]?.[targetPairIndex], slot, loser, loserRating, loserStars);
    }

    // Losers of WB round r face LB survivors in LB R(2*(r-1)); LB pair counts match WB round pair counts
    const targetIndex = stageLabels.indexOf(`LB R${2 * (wbRound - 1)}`);
    return fillSlot(updatedPairs[targetIndex]?.[pairIndex], 'team2', loser, loserRating, loserStars);
};

export const promoteLoserBracketWinner = ({
    updatedPairs,
    stageLabels,
    currentStage,
    pairIndex,
    winner,
    winnerRating,
    winnerStars
}) => {
    if (!winner || winner === 'TBD' || !currentStage.startsWith('LB')) {
        return false;
    }

    if (currentStage === 'LB Final') {
        const grandFinalIndex = stageLabels.indexOf('Grand Final');
        if (grandFinalIndex === -1) {
            return false;
        }
        return fillSlot(updatedPairs[grandFinalIndex]?.[0], 'team2', winner, winnerRating, winnerStars);
    }

    if (currentStage.startsWith('LB R')) {
        const roundNumber = Number(currentStage.replace('LB R', ''));
        const nextLabel = stageLabels.includes(`LB R${roundNumber + 1}`) ? `LB R${roundNumber + 1}` : 'LB Final';
        const currentIndex = stageLabels.indexOf(currentStage);
        const nextIndex = stageLabels.indexOf(nextLabel);
        if (nextIndex === -1) {
            return false;
        }

        const currentPairCount = updatedPairs[currentIndex]?.length || 0;
        const nextPairCount = updatedPairs[nextIndex]?.length || 0;

        if (nextPairCount === currentPairCount) {
            // Consolidation round: same pair count, LB winner takes team1 and meets a WB dropper (team2)
            return fillSlot(updatedPairs[nextIndex]?.[pairIndex], 'team1', winner, winnerRating, winnerStars);
        }

        // Merge round: pair count halves, two LB winners meet each other
        const targetPairIndex = Math.floor(pairIndex / 2);
        const slot = pairIndex % 2 === 0 ? 'team1' : 'team2';
        return fillSlot(updatedPairs[nextIndex]?.[targetPairIndex], slot, winner, winnerRating, winnerStars);
    }

    return false;
};

/**
 * Who should be recorded as tournament 3rd place after a match report.
 * - Single-elim "Third Place" match → series winner
 * - Double-elim "LB Final" → series loser (winner advances to Grand Final)
 * - Otherwise → null (this report does not set 3rd place)
 */
export const resolveThirdPlaceFinisher = ({
    hasLoserBracket = false,
    stage,
    winner,
    team1,
    team2
}) => {
    if (!winner || !team1 || !team2) {
        return null;
    }

    if (stage === 'Third Place') {
        return winner;
    }

    if (hasLoserBracket && stage === 'LB Final') {
        return team1 === winner ? team2 : team1;
    }

    return null;
};

export const shouldAwardThirdPlaceForStage = (hasLoserBracket, stage) =>
    stage === 'Third Place' || (Boolean(hasLoserBracket) && stage === 'LB Final');

export const isPlaceholderWinnerName = (name) => {
    const value = String(name || '').trim();
    return !value || value === 'TBD' || value === 'BYE' || value === 'null';
};

const placeSortRank = (place) => {
    const raw = String(place || '')
        .toLowerCase()
        .trim();
    if (raw.startsWith('1') || raw.includes('1st')) {
        return 1;
    }
    if (raw.startsWith('2') || raw.includes('2nd')) {
        return 2;
    }
    if (raw.startsWith('3') || raw.includes('3rd')) {
        return 3;
    }
    return 99;
};

/** Winner slots that should appear on finished-cup cards (drops TBD / empty). */
export const getDisplayableWinnerEntries = (winners) => {
    if (!winners || typeof winners !== 'object') {
        return [];
    }

    return Object.entries(winners)
        .filter(([, winner]) => !isPlaceholderWinnerName(winner))
        .sort(([placeA], [placeB]) => placeSortRank(placeA) - placeSortRank(placeB));
};

/**
 * Infer 3rd place from an already-completed LB Final / Third Place pair in the bracket.
 * Used to backfill winners when prize payout skipped writing the placement name.
 */
export const resolveThirdPlaceFromPlayoffPairs = (playoffPairs, { hasLoserBracket = false } = {}) => {
    if (!Array.isArray(playoffPairs)) {
        return null;
    }

    const pairs = playoffPairs.flat().filter(Boolean);
    const targetStage = hasLoserBracket ? 'LB Final' : 'Third Place';
    const match = pairs.find(
        (pair) =>
            pair?.stage === targetStage &&
            pair?.winner &&
            pair.winner !== 'draw' &&
            !isPlaceholderWinnerName(pair.team1) &&
            !isPlaceholderWinnerName(pair.team2)
    );

    if (!match) {
        return null;
    }

    return resolveThirdPlaceFinisher({
        hasLoserBracket,
        stage: match.stage,
        winner: match.winner,
        team1: match.team1,
        team2: match.team2
    });
};

